/**
 * Copyright (c) Microsoft. All rights reserved.s
 */

import { action, computed, observable } from 'mobx';
import { FeaturesSettings } from 'src/core/services/accountSettingsService';
import { AzureTokens } from 'src/core/stores/AzureSessionStore';
import { ProgressTrackerStore } from 'src/core/stores/ProgressTrackerStore';
import { Region } from 'src/core/types/regionTypes';
import { getEnvironment } from 'src/core/utils/api/environment';
import {
    getResourcePathData,
    isCluResource,
    isCustomHealthcareResource,
    isCustomTextResource,
    isSummarizationResource,
    isTextAnalyticsResource
} from 'src/core/utils/csAccountUtils';

import { getPortalFromUrl } from 'src/core/utils/routingUtils';

export type AzureCsAccountKind = 'LUIS' | 'LUIS.Authoring' | 'CognitiveServices' | 'TextAnalytics' | 'TextTranslation' | 'OpenAI';
export type AzureCsAccountProviders = 'Microsoft.CognitiveServices' | 'Microsoft.MachineLearningServices';

/**
 * Also known as an Azure Cognitive Service Account, or an Azure Subscription Key,
 * this represents the unit of billing used to power LUIS API
 * and endpoint calls.
 */
export class AzureCsAccountStore {
    public id: string;

    public path: string;

    public name: string;

    public region: Region;

    public endpoint: string;

    public principalId: string;

    public customDomain: string;

    public kind: AzureCsAccountKind;

    public providers: AzureCsAccountProviders;

    public readonly featureSettingsTracker = new ProgressTrackerStore();

    @observable public sku: AzureSku;

    @observable public ownedStorageId: string;

    @observable public isIdentityEnabled: boolean;

    @observable public identityType: 'None' | 'SystemAssigned' | 'UserAssigned' | 'SystemAssigned, UserAssigned';

    @observable public accountTokens: AzureTokens;

    @observable public ownedSearchResourceId: string;

    @observable public ownedAzureOpenAiResourceId: string;

    @observable public featuresSettings: FeaturesSettings;

    @observable public allowedSkuCodes: AzureSku['code'][];

    @observable public keyStrings: { key1: string; key2: string };

    constructor(id: string, data: Partial<Omit<AzureCsAccountStore, 'id' | 'subscriptionId' | 'resourceGroupName'>>) {
        this.id = id;
        this.path = data.path;
        this.name = data.name;
        this.kind = data.kind;
        this.providers = data.providers;
        this.region = data.region;
        this.identityType = data.identityType;
        this.endpoint = data.endpoint;
        this.principalId = data.principalId;
        this.sku = data.sku ?? { code: null };
        this.ownedStorageId = data.ownedStorageId;
        this.customDomain = data.customDomain ?? null;
        this.isIdentityEnabled = data.isIdentityEnabled ?? null;
        this.allowedSkuCodes = data.allowedSkuCodes ?? [];
        this.ownedSearchResourceId = data.ownedSearchResourceId;
        this.keyStrings = data.keyStrings ?? { key1: null, key2: null };
        this.ownedAzureOpenAiResourceId = data.ownedAzureOpenAiResourceId;
        this.featuresSettings = data.featuresSettings ?? { deepStackTraining: false };
        this.accountTokens = data.accountTokens ?? { armToken: null, storageToken: null };
    }

    @computed
    public get subscriptionId() {
        const pathSegments = this.path.split('/');
        const subscriptionIndex = pathSegments.findIndex(s => s.toLocaleLowerCase() === 'subscriptions');

        return pathSegments[subscriptionIndex + 1];
    }

    @computed
    public get resourceGroupName() {
        const pathSegments = this.path.split('/');
        const resourceGroupIndex = pathSegments.findIndex(s => s.toLocaleLowerCase() === 'resourcegroups');

        return pathSegments[resourceGroupIndex + 1];
    }

    @computed
    public get serviceKey() {
        const { isProduction } = getEnvironment();
        const { subPath } = getPortalFromUrl();

        if (this.kind === 'TextTranslation') {
            return this.keyStrings.key1;
        }

        /**
         * Language Studio Demos use production APIs that we do
         * not have access to, so use key string regardless of
         * staging or production.
         */
        if (this.kind === 'TextAnalytics' && subPath === 'root') {
            return this.keyStrings.key1;
        }

        return isProduction ? this.keyStrings.key1 : this.id;
    }

    @computed
    public get isTextAnalyticsResourceKind() {
        return isTextAnalyticsResource(this);
    }

    @computed
    public get isCustomTextCompatibleResource() {
        return isCustomTextResource(this);
    }

    @computed
    public get isSummarizationCompatibleResource() {
        return isSummarizationResource(this);
    }

    @computed
    public get isCustomHealthcareCompatibleResource() {
        return isCustomHealthcareResource(this);
    }

    @computed
    public get isCluCompatibleResource() {
        return isCluResource(this);
    }

    @computed
    public get ownedStorageAccountInfo() {
        return this.ownedStorageId ? getResourcePathData(this.ownedStorageId) : { subscriptionId: '', resourceGroupName: '', name: '' };
    }

    @computed
    public get ownedSearchResourceInfo() {
        return this.ownedSearchResourceId
            ? getResourcePathData(this.ownedSearchResourceId)
            : { subscriptionId: '', resourceGroupName: '', name: '' };
    }

    @computed
    public get ownedAzureOpenAiResourceInfo() {
        return this.ownedAzureOpenAiResourceId
            ? getResourcePathData(this.ownedAzureOpenAiResourceId)
            : { subscriptionId: '', resourceGroupName: '', name: '' };
    }

    /**
     * Sets the owned storage Id
     */
    @action
    public setOwnedStorageId(storageId: string) {
        this.ownedStorageId = storageId;
    }

    /**
     * Sets the owned azure search resource Id
     */
    @action
    public setOwnedSearchResourceId(resourceId: string) {
        this.ownedSearchResourceId = resourceId;
    }

    /**
     * Sets the owned azure open ai resource Id
     */
    @action
    public setOwnedAzureOpenAiResourceId(resourceId: string) {
        this.ownedAzureOpenAiResourceId = resourceId;
    }

    /**
     * Sets the sku
     */
    @action
    public setSku(code: AzureSku['code']) {
        this.sku = { code, name: code === 'F0' || code === 'E0' ? 'Free' : 'Standard' };
    }

    /**
     * Sets the isIdentityEnabled flag
     */
    @action
    public setIsIdentityEnabledFlag(isIdentityEnabled: boolean) {
        this.isIdentityEnabled = isIdentityEnabled;
    }

    /**
     * Sets the keyStrings object
     */
    @action
    public setKeyStringsObject(keyStringsObject: { key1: string; key2: string }) {
        this.keyStrings = keyStringsObject;
    }

    /**
     * Sets the featuresSettings object
     */
    @action
    public setFeaturesSettingsObject(featuresSettingsObject: FeaturesSettings) {
        this.featuresSettings = featuresSettingsObject;
    }

    /**
     * Sets the allowed sku codes list.
     */
    @action
    public setAllowedSkuCodes(skuCodes: AzureSku['code'][]) {
        this.allowedSkuCodes = skuCodes;
    }

    /**
     * Sets the account tenant information to the given
     * information object.
     *
     * @param info The information to set.
     */
    @action
    public setAccountTokens(tokens: AzureTokens) {
        this.accountTokens = tokens;
    }
}

export type AzureSku = {
    /**
     * The pricing code for the account.
     */
    code: 'F0' | 'F1' | 'S0' | 'S1' | 'E0' | 'S' | 'Basic' | 'D3';

    /**
     * The pricing tier readable name.
     */
    name?: 'Free' | 'Standard' | 'Basic';
};
